import React, { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { UnsavedChangesProvider } from "@/contexts/UnsavedChangesContext";
import { PageLoader } from "@/components/ui/page-loader";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";

// Critical routes loaded immediately for fast initial render
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Signup from "./pages/Signup";
import SignupConfirmation from "./pages/SignupConfirmation";
import InvitationPending from "./pages/InvitationPending";
import InvitationExpired from "./pages/InvitationExpired";
import NotFound from "./pages/NotFound";

// Lazy-loaded routes for better code splitting
const Profile = lazy(() => import("./pages/Profile"));
const Tickets = lazy(() => import("./pages/Tickets"));
const TicketDetail = lazy(() => import("./pages/TicketDetail"));
const Agents = lazy(() => import("./pages/Agents"));
const ResponseTemplates = lazy(() => import("./pages/ResponseTemplates"));
const FAQ = lazy(() => import("./pages/FAQ"));
const PublicHelp = lazy(() => import("./pages/PublicHelp"));
const PublicTicket = lazy(() => import("./pages/PublicTicket"));
const Customers = lazy(() => import("./pages/Customers"));
const CustomerDetail = lazy(() => import("./pages/CustomerDetail"));

const Settings = lazy(() => import("./pages/Settings"));
const Offer = lazy(() => import("./pages/Offer"));
const Statistics = lazy(() => import("./pages/Statistics"));
const RateTicket = lazy(() => import("./pages/RateTicket"));
const Widget = lazy(() => import("./pages/Widget"));
const FormWidget = lazy(() => import("./pages/FormWidget"));
const AIChatbotWidget = lazy(() => import("./pages/AIChatbotWidget"));
const Sitemap = lazy(() => import("./pages/Sitemap"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const PublicApiDocs = lazy(() => import("./pages/PublicApiDocs"));

// Admin routes - lazy loaded as they're rarely accessed
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminWorkspaces = lazy(() => import("./pages/admin/AdminWorkspaces"));
const AdminWorkspaceDetail = lazy(() => import("./pages/admin/AdminWorkspaceDetail"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminSubscriptions = lazy(() => import("./pages/admin/AdminSubscriptions"));
const AdminDocumentation = lazy(() => import("./pages/admin/AdminDocumentation"));
const AdminDesignSystem = lazy(() => import("./pages/admin/AdminDesignSystem"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminAnnouncements = lazy(() => import("./pages/admin/AdminAnnouncements"));
const AdminAuditLogs = lazy(() => import("./pages/admin/AdminAuditLogs"));
const AdminEmailPreviews = lazy(() => import("./pages/admin/AdminEmailPreviews"));
const AdminTips = lazy(() => import("./pages/admin/AdminTips"));
const AdminEmailStats = lazy(() => import("./pages/admin/AdminEmailStats"));
const AdminEmailDocumentation = lazy(() => import("./pages/admin/AdminEmailDocumentation"));
const AdminAIPoints = lazy(() => import("./pages/admin/AdminAIPoints"));
const AdminAIUsage = lazy(() => import("./pages/admin/AdminAIUsage"));
const AdminWorkspaceAIUsage = lazy(() => import("./pages/admin/AdminWorkspaceAIUsage"));
const AdminPlanLimits = lazy(() => import("./pages/admin/AdminPlanLimits"));
const AdminPayments = lazy(() => import("./pages/admin/AdminPayments"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30 * 1000, // Data stays fresh for 30 seconds
      gcTime: 5 * 60 * 1000, // Cache kept for 5 minutes
    },
  },
});

const App = () => (
  <AppErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <UnsavedChangesProvider>
            <Suspense fallback={<PageLoader />}>
              <Routes>
              {/* Critical routes - not lazy loaded */}
              <Route path="/" element={<Index />} />
              <Route path="/space/:spaceId" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/signup/confirmation" element={<SignupConfirmation />} />
              <Route path="/invitation/pending" element={<InvitationPending />} />
              <Route path="/invitation/expired" element={<InvitationExpired />} />
              <Route path="/onboarding" element={<Onboarding />} />
              
              {/* Main app routes - lazy loaded */}
              <Route path="/tickets" element={<Tickets />} />
              <Route path="/tickets/:ticketId" element={<TicketDetail />} />
              <Route path="/agents" element={<Agents />} />
              <Route path="/responses" element={<ResponseTemplates />} />
              <Route path="/customers" element={<Customers />} />
              <Route path="/customers/:id" element={<CustomerDetail />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/offer" element={<Offer />} />
              <Route path="/statistics" element={<Statistics />} />
              {/* Redirect legacy /api route to settings */}
              <Route path="/api" element={<Navigate to="/settings?tab=api" replace />} />
              
              {/* Public routes - lazy loaded */}
              <Route path="/espace/:workspaceSlug" element={<PublicHelp />} />
              <Route path="/espace/:workspaceSlug/faq" element={<PublicHelp />} />
              <Route path="/espace/:workspaceSlug/contact" element={<PublicHelp />} />
              <Route path="/espace/:workspaceSlug/mes-tickets" element={<PublicHelp />} />
              <Route path="/espace/:workspaceSlug/faq/:categoryId" element={<PublicHelp />} />
              <Route path="/espace/:workspaceSlug/ticket/:ticketId/:token" element={<PublicTicket />} />
              <Route path="/espace/:workspaceSlug/widget" element={<Widget />} />
              <Route path="/espace/:workspaceSlug/form" element={<FormWidget />} />
              <Route path="/espace/:workspaceSlug/ai-chat" element={<AIChatbotWidget />} />
              <Route path="/rate/:ticketId/:token" element={<RateTicket />} />
              
              {/* Admin routes - lazy loaded */}
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/workspaces" element={<AdminWorkspaces />} />
              <Route path="/admin/workspaces/:id" element={<AdminWorkspaceDetail />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/subscriptions" element={<AdminSubscriptions />} />
              <Route path="/admin/emails" element={<AdminEmailPreviews />} />
              <Route path="/admin/email-stats" element={<AdminEmailStats />} />
              <Route path="/admin/documentation" element={<AdminDocumentation />} />
              <Route path="/admin/design-system" element={<AdminDesignSystem />} />
              <Route path="/admin/settings" element={<AdminSettings />} />
              <Route path="/admin/announcements" element={<AdminAnnouncements />} />
              <Route path="/admin/audit-logs" element={<AdminAuditLogs />} />
              <Route path="/admin/tips" element={<AdminTips />} />
              <Route path="/admin/email-docs" element={<AdminEmailDocumentation />} />
              <Route path="/admin/ai-points" element={<AdminAIPoints />} />
              <Route path="/admin/ai-usage" element={<AdminAIUsage />} />
              <Route path="/admin/ai-usage/:workspaceId" element={<AdminWorkspaceAIUsage />} />
              <Route path="/admin/plan-limits" element={<AdminPlanLimits />} />
              <Route path="/admin/payments" element={<AdminPayments />} />
              
              {/* Utility routes */}
              <Route path="/sitemap.xml" element={<Sitemap />} />
              <Route path="/docs/api" element={<PublicApiDocs />} />
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </UnsavedChangesProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </AppErrorBoundary>
);

export default App;
